// WhatsAppManager.js
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers, faSlidersH, faChevronDown, faChevronUp,
  faSpinner, faSearch, faTimes, faArrowLeft, faCamera,
  faComment, faInbox
} from "@fortawesome/free-solid-svg-icons";
import './WhatsAppSenderModal.css';
import Sidebar from "./Sidebar";

// Helper: format timestamp (same as mobile app)
const formatTimestamp = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();

  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  const yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return "Yesterday";
  }
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 7) {
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  }
  return date.toLocaleDateString([], { day: 'numeric', month: 'numeric', year: '2-digit' });
};

const WhatsAppManager = () => {
  const navigate = useNavigate();

  // ========== State ==========
  const [parents, setParents] = useState([]);
  const [loadingParents, setLoadingParents] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    name: '',
    mobile: '',
    class: '',
    section: '',
  });

  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);

  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [mobileSuggestions, setMobileSuggestions] = useState([]);
  const [showNameSuggestions, setShowNameSuggestions] = useState(false);
  const [showMobileSuggestions, setShowMobileSuggestions] = useState(false);
  const nameInputRef = useRef(null);
  const mobileInputRef = useRef(null);
  const searchTimeout = useRef(null);

  // Chat list state
  const [chatList, setChatList] = useState([]);
  const [loadingChats, setLoadingChats] = useState(false);
  const [currentUserMobile, setCurrentUserMobile] = useState(null);
  const [currentUserName, setCurrentUserName] = useState('');

  // Tab state: 'inbox' or 'all'
  const [activeTab, setActiveTab] = useState('inbox');

  // Profile data
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [profileNickname, setProfileNickname] = useState('');
  const [profileBio, setProfileBio] = useState('');
  const [profileAvatarUrl, setProfileAvatarUrl] = useState(null);
  const [mainAvatarUrl, setMainAvatarUrl] = useState(null);
  const [avatarLocalUri, setAvatarLocalUri] = useState(null);
  const [initialNickname, setInitialNickname] = useState('');
  const [initialBio, setInitialBio] = useState('');
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const avatarInputRef = useRef(null);

  const hasProfileChanges =
    profileNickname !== initialNickname ||
    profileBio !== initialBio ||
    avatarLocalUri !== null;

  const API_BASE = "https://mhsdev.online/MHS-WEB/api";

  // ========== Load current user data ==========
  useEffect(() => {
    const mobile = localStorage.getItem('userMobile');
    const name = localStorage.getItem('userName');
    setCurrentUserMobile(mobile);
    setCurrentUserName(name || 'Admin');

    if (mobile) {
      fetchMainUserProfile(mobile);
    }
  }, []);

  const fetchMainUserProfile = async (mobile) => {
    try {
      const response = await axios.get(`${API_BASE}/mhs_wa_profile_api.php`, {
        params: { action: 'get_profile', mobile: mobile },
        timeout: 10000,
      });
      const data = response.data;
      if (data.avatar_url) {
        setMainAvatarUrl(data.avatar_url);
      }
    } catch (error) {
      console.error("Error fetching main profile:", error);
    }
  };

  // ========== Data Fetching ==========
  useEffect(() => {
    fetchClasses();
    fetchParents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (filters.class) {
      fetchSections(filters.class);
    } else {
      setSections([]);
    }
    fetchParents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.class, filters.section]);

  useEffect(() => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      fetchParents();
    }, 400);
    return () => clearTimeout(searchTimeout.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.name, filters.mobile]);

  useEffect(() => {
    if (currentUserMobile) {
      fetchChatList();
    } else {
      console.warn('currentUserMobile is null – chat list will not be fetched.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserMobile]);

  useEffect(() => {
    if (isProfileModalOpen && currentUserMobile) {
      fetchUserProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProfileModalOpen, currentUserMobile]);

  const fetchUserProfile = async () => {
    if (!currentUserMobile) return;
    setIsLoadingProfile(true);
    try {
      const response = await axios.get(`${API_BASE}/mhs_wa_profile_api.php`, {
        params: { action: 'get_profile', mobile: currentUserMobile },
        timeout: 10000,
      });
      const data = response.data;
      setProfileNickname(data.nickname || '');
      setProfileBio(data.bio || '');
      setInitialNickname(data.nickname || '');
      setInitialBio(data.bio || '');
      setProfileAvatarUrl(data.avatar_url || null);
      setMainAvatarUrl(data.avatar_url || null);
      setAvatarLocalUri(null);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API_BASE}/broadcast_api.php?action=list_classes`);
      setClasses(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  const fetchSections = async (cls) => {
    if (!cls) {
      setSections([]);
      return;
    }
    try {
      const response = await axios.get(`${API_BASE}/broadcast_api.php?action=list_sections&class=${cls}`);
      setSections(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching sections:", error);
    }
  };

  const fetchParents = async () => {
    setLoadingParents(true);
    try {
      const params = {
        action: 'list_students',
        class: filters.class || '',
        section: filters.section || '',
        search: filters.name || '',
      };
      const response = await axios.get(`${API_BASE}/broadcast_api.php`, { params });
      const data = Array.isArray(response.data) ? response.data : [];
      setParents(data);

      const uniqueNames = [...new Set(data.map(p => p.name).filter(Boolean))];
      const uniqueMobiles = [...new Set(data.map(p => p.mobile).filter(Boolean))];
      setNameSuggestions(uniqueNames);
      setMobileSuggestions(uniqueMobiles);
    } catch (error) {
      console.error("Error fetching parents:", error);
    } finally {
      setLoadingParents(false);
    }
  };

  const fetchChatList = async () => {
    if (!currentUserMobile) return;
    setLoadingChats(true);
    try {
      const response = await axios.get(`${API_BASE}/messages_api.php`, {
        params: {
          action: 'get_chat_list',
          user_mobile: currentUserMobile,
        },
        timeout: 10000,
      });
      if (response.data && Array.isArray(response.data)) {
        setChatList(response.data);
      } else {
        setChatList([]);
      }
    } catch (error) {
      console.error('Error fetching chat list:', error);
      setChatList([]);
    } finally {
      setLoadingChats(false);
    }
  };

  // ========== Filter Handlers ==========
  const handleNameChange = (e) => {
    const value = e.target.value;
    setFilters({ ...filters, name: value });
    setShowNameSuggestions(true);
  };

  const handleMobileChange = (e) => {
    const value = e.target.value;
    setFilters({ ...filters, mobile: value });
    setShowMobileSuggestions(true);
  };

  const selectNameSuggestion = (name) => {
    setFilters({ ...filters, name });
    setShowNameSuggestions(false);
  };

  const selectMobileSuggestion = (mobile) => {
    setFilters({ ...filters, mobile });
    setShowMobileSuggestions(false);
  };

  const clearAllFilters = () => {
    setFilters({ name: '', mobile: '', class: '', section: '' });
  };

  const filteredNameSuggestions = nameSuggestions.filter(name =>
    name.toLowerCase().includes(filters.name.toLowerCase())
  );
  const filteredMobileSuggestions = mobileSuggestions.filter(mobile =>
    mobile.includes(filters.mobile)
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (nameInputRef.current && !nameInputRef.current.contains(event.target)) {
        setShowNameSuggestions(false);
      }
      if (mobileInputRef.current && !mobileInputRef.current.contains(event.target)) {
        setShowMobileSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openChat = (parent) => {
    navigate(`/chat/${parent.id}`, {
      state: {
        otherUserId: parent.id,
        otherUserMobile: parent.mobile,
        otherUserName: parent.name,
        otherUserType: 'parent',
      }
    });
  };

  // Helper: enrich a parent with chat data (if any)
  const getChatData = (mobile) => {
    const normalizedMobile = mobile.replace(/-/g, '');
    return chatList.find(chat =>
      chat.other_party_mobile.replace(/-/g, '') === normalizedMobile
    );
  };

  // ========== Profile handlers ==========
  const handlePickAvatar = () => {
    if (avatarInputRef.current) {
      avatarInputRef.current.click();
    }
  };

  const handleAvatarFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarLocalUri(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveProfile = async () => {
    if (!currentUserMobile) {
      alert('Error: User mobile not loaded.');
      return;
    }
    if (profileNickname.trim().length === 0) {
      alert('Name required. Please enter a name before saving.');
      return;
    }

    setIsSavingProfile(true);
    try {
      const formData = new FormData();
      formData.append('mobile', currentUserMobile);
      formData.append('nickname', profileNickname.trim());
      formData.append('bio', profileBio.trim());

      if (avatarLocalUri) {
        const response = await fetch(avatarLocalUri);
        const blob = await response.blob();
        const fileExtension = blob.type.split('/')[1] || 'jpg';
        formData.append('avatar', blob, `avatar.${fileExtension}`);
      }

      const response = await axios.post(
        `${API_BASE}/mhs_wa_profile_api.php?action=update_profile`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 15000 }
      );

      if (response.data?.success) {
        setInitialNickname(profileNickname.trim());
        setInitialBio(profileBio.trim());
        if (response.data.profile?.avatar_url) {
          setProfileAvatarUrl(response.data.profile.avatar_url);
          setMainAvatarUrl(response.data.profile.avatar_url);
        }
        setAvatarLocalUri(null);
        setCurrentUserName(profileNickname.trim());
        alert('Saved! Your profile has been updated.');
      } else {
        alert('Error: ' + (response.data?.error || 'Failed to save profile.'));
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Error: Could not save your profile.');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const displayedAvatar = avatarLocalUri || profileAvatarUrl;

  const handleOpenBroadcast = () => {
    navigate('/broadcast');
  };

  const activeFilterCount = ['name', 'mobile', 'class', 'section'].filter(
    (key) => filters[key]
  ).length;

  // ========== Render ==========
  return (
    <div className="wam-shell">
      <Sidebar />

      <div className="wam-main">
        {/* ----- Header ----- */}
        <header className="wam-header">
          <div className="wam-header-left">
            <span className="wam-header-icon">
              <FontAwesomeIcon icon={faUsers} />
            </span>
            <div>
              <h1 className="wam-header-title">Parent Directory</h1>
              <p className="wam-header-subtitle">
                {loadingParents ? 'Syncing roster…' : `${parents.length} parents on record`}
              </p>
            </div>
          </div>

          <div className="wam-header-right">
            <span className="wam-live-badge">
              <span className="wam-pulse-dot"></span> Live
            </span>

            <button
              className="wam-profile-chip"
              onClick={() => setIsProfileModalOpen(true)}
            >
              <span className="wam-profile-chip-avatar">
                {mainAvatarUrl ? (
                  <img src={mainAvatarUrl} alt="Profile" />
                ) : (
                  <span>{currentUserName ? currentUserName.charAt(0).toUpperCase() : 'U'}</span>
                )}
              </span>
              <span className="wam-profile-chip-text">
                <span className="wam-profile-chip-name">{currentUserName || 'Admin'}</span>
                <span className="wam-profile-chip-mobile">{currentUserMobile || ''}</span>
              </span>
            </button>
          </div>
        </header>

        {/* ----- Search + filters ----- */}
        <div className="wam-toolbar">
          <div className="wam-toolbar-row">
            <div className="wam-search-field">
              <FontAwesomeIcon icon={faSearch} className="wam-search-icon" />
              <input
                type="text"
                placeholder="Search by name or mobile…"
                value={filters.name}
                onChange={handleNameChange}
              />
              {filters.name && (
                <button
                  className="wam-search-clear"
                  onClick={() => setFilters({ ...filters, name: '' })}
                  aria-label="Clear search"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>

            <button
              className={`wam-filter-toggle ${showFilters ? 'is-open' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <FontAwesomeIcon icon={faSlidersH} />
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <span className="wam-filter-count">{activeFilterCount}</span>
              )}
              <FontAwesomeIcon icon={showFilters ? faChevronUp : faChevronDown} />
            </button>
          </div>

          {showFilters && activeTab === 'all' && (
            <div className="wam-filter-panel">
              <div className="wam-filter-grid">
                <div className="wam-filter-field" ref={mobileInputRef}>
                  <span className="wam-eyebrow">Mobile</span>
                  <div className="wam-autocomplete">
                    <input
                      type="text"
                      placeholder="Search by mobile…"
                      value={filters.mobile}
                      onChange={handleMobileChange}
                      onFocus={() => setShowMobileSuggestions(true)}
                    />
                    {showMobileSuggestions && filteredMobileSuggestions.length > 0 && (
                      <ul className="wam-suggestions">
                        {filteredMobileSuggestions.map(mobile => (
                          <li key={mobile} onClick={() => selectMobileSuggestion(mobile)}>
                            {mobile}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div className="wam-filter-field" ref={nameInputRef}>
                  <span className="wam-eyebrow">Name</span>
                  <div className="wam-autocomplete">
                    {showNameSuggestions && filters.name && filteredNameSuggestions.length > 0 && (
                      <ul className="wam-suggestions">
                        {filteredNameSuggestions.map(name => (
                          <li key={name} onClick={() => selectNameSuggestion(name)}>
                            {name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div className="wam-filter-field">
                  <span className="wam-eyebrow">Class</span>
                  <select
                    value={filters.class}
                    onChange={(e) => setFilters({ ...filters, class: e.target.value })}
                  >
                    <option value="">All classes</option>
                    {classes.map(cls => <option key={cls} value={cls}>{cls}</option>)}
                  </select>
                </div>

                <div className="wam-filter-field">
                  <span className="wam-eyebrow">Section</span>
                  <select
                    value={filters.section}
                    onChange={(e) => setFilters({ ...filters, section: e.target.value })}
                    disabled={!filters.class || sections.length === 0}
                  >
                    <option value="">All sections</option>
                    {sections.map(sec => <option key={sec} value={sec}>{sec}</option>)}
                  </select>
                </div>

                {activeFilterCount > 0 && (
                  <button className="wam-clear-filters" onClick={clearAllFilters}>
                    Clear all
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ----- Tab Switcher ----- */}
        <div className="wam-tabs">
          <button
            className={`wam-tab ${activeTab === 'inbox' ? 'active' : ''}`}
            onClick={() => setActiveTab('inbox')}
          >
            <FontAwesomeIcon icon={faInbox} /> Inbox
          </button>
          <button
            className={`wam-tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            <FontAwesomeIcon icon={faUsers} /> All Parents
          </button>
        </div>

        {/* ----- List ----- */}
        <div className="wam-list-container">
          {activeTab === 'inbox' ? (
            // --- Inbox View ---
            loadingChats ? (
              <div className="wam-state">
                <FontAwesomeIcon icon={faSpinner} spin className="wam-state-spinner" />
                <p>Loading conversations…</p>
              </div>
            ) : chatList.length === 0 ? (
              <div className="wam-state">
                <FontAwesomeIcon icon={faInbox} className="wam-state-icon" />
                <p>No conversations yet.</p>
                <span className="wam-state-hint">Start a chat from the All Parents list.</span>
              </div>
            ) : (
              <div className="wam-list">
                {chatList.map((chat) => {
                  // Build a parent-like object from chat data
                  const parent = {
                    id: chat.other_party_mobile, // we use mobile as id for inbox row
                    name: chat.other_party_name || 'Unknown',
                    mobile: chat.other_party_mobile,
                    student_id: '', // not available from chat list
                  };
                  return (
                    <div key={chat.other_party_mobile} className="wam-row" onClick={() => openChat(parent)}>
                      <span className="wam-row-avatar">
                        {chat.avatar_url ? (
                          <img src={chat.avatar_url} alt={parent.name} />
                        ) : (
                          parent.name.charAt(0).toUpperCase()
                        )}
                      </span>

                      <span className="wam-row-content">
                        <span className="wam-row-top">
                          <span className="wam-row-name">{parent.name}</span>
                          <span className="wam-row-meta">
                            <span className="wam-row-time">
                              {formatTimestamp(chat.last_message_time)}
                            </span>
                            {parseInt(chat.unread_count || 0) > 0 && (
                              <span className="wam-unread-badge">{chat.unread_count}</span>
                            )}
                          </span>
                        </span>
                        <span className="wam-row-bottom">
                          <span className={`wam-row-message ${parseInt(chat.unread_count || 0) > 0 ? 'is-unread' : ''}`}>
                            {chat.last_message || 'No messages yet'}
                          </span>
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            // --- All Parents View ---
            loadingParents ? (
              <div className="wam-state">
                <FontAwesomeIcon icon={faSpinner} spin className="wam-state-spinner" />
                <p>Loading parents…</p>
              </div>
            ) : parents.length === 0 ? (
              <div className="wam-state">
                <FontAwesomeIcon icon={faUsers} className="wam-state-icon" />
                <p>No parents found.</p>
                <span className="wam-state-hint">Try adjusting your search or filters.</span>
              </div>
            ) : (
              <div className="wam-list">
                {parents.map((p) => {
                  const chat = getChatData(p.mobile);
                  const lastMessage = chat?.last_message || 'No messages yet';
                  const timestamp = chat?.last_message_time ? formatTimestamp(chat.last_message_time) : '';
                  const unreadCount = parseInt(chat?.unread_count || 0);
                  const isRead = unreadCount === 0;
                  const parentAvatar = chat?.avatar_url;

                  return (
                    <div key={p.id} className="wam-row" onClick={() => openChat(p)}>
                      <span className="wam-row-avatar">
                        {parentAvatar ? (
                          <img src={parentAvatar} alt={p.name} />
                        ) : (
                          p.name.charAt(0).toUpperCase()
                        )}
                      </span>

                      <span className="wam-row-content">
                        <span className="wam-row-top">
                          <span className="wam-row-name">{p.name}</span>
                          <span className="wam-row-meta">
                            <span className="wam-row-time">{timestamp}</span>
                            {!isRead && unreadCount > 0 && (
                              <span className="wam-unread-badge">{unreadCount}</span>
                            )}
                          </span>
                        </span>
                        <span className="wam-row-bottom">
                          <span className={`wam-row-message ${isRead ? '' : 'is-unread'}`}>
                            {lastMessage}
                          </span>
                          <span className="wam-id-badge">{p.student_id}</span>
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
            )
          )}
        </div>

        {/* ----- Floating broadcast button ----- */}
        <button className="wam-fab" onClick={handleOpenBroadcast}>
          <span className="wam-fab-tooltip">New broadcast</span>
          <FontAwesomeIcon icon={faComment} />
        </button>

        {/* ----- Profile modal (unchanged) ----- */}
        {isProfileModalOpen && (
          <div className="wam-modal-overlay" onClick={() => setIsProfileModalOpen(false)}>
            <div className="wam-modal" onClick={(e) => e.stopPropagation()}>
              <div className="wam-modal-header">
                <button
                  className="wam-modal-back"
                  onClick={() => setIsProfileModalOpen(false)}
                  aria-label="Close"
                >
                  <FontAwesomeIcon icon={faArrowLeft} />
                </button>
                <span className="wam-modal-title">Profile</span>
                <button
                  className="wam-modal-save"
                  onClick={handleSaveProfile}
                  disabled={!hasProfileChanges || isSavingProfile}
                >
                  {isSavingProfile ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Save'}
                </button>
              </div>

              <div className="wam-modal-body">
                {isLoadingProfile ? (
                  <div className="wam-modal-loading">
                    <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                  </div>
                ) : (
                  <>
                    <div className="wam-avatar-section">
                      <div className="wam-avatar-upload" onClick={handlePickAvatar}>
                        <input
                          type="file"
                          accept="image/*"
                          ref={avatarInputRef}
                          style={{ display: 'none' }}
                          onChange={handleAvatarFileChange}
                        />
                        {displayedAvatar ? (
                          <img src={displayedAvatar} alt="Profile" />
                        ) : (
                          <span className="wam-avatar-placeholder">
                            {(profileNickname || currentUserName || 'U').charAt(0).toUpperCase()}
                          </span>
                        )}
                        <span className="wam-avatar-camera">
                          <FontAwesomeIcon icon={faCamera} />
                        </span>
                      </div>
                      <p className="wam-avatar-hint">Tap to change photo</p>
                    </div>

                    <div className="wam-modal-field">
                      <span className="wam-eyebrow">Name</span>
                      <input
                        type="text"
                        value={profileNickname}
                        onChange={(e) => setProfileNickname(e.target.value.slice(0, 60))}
                        placeholder="Enter your name"
                        maxLength={60}
                        className="wam-modal-input"
                      />
                      <div className="wam-char-count">{profileNickname.length}/60</div>
                    </div>
                    <p className="wam-field-hint">
                      This is not your username or PIN. This name will be visible to your contacts.
                    </p>

                    <div className="wam-modal-field">
                      <span className="wam-eyebrow">About</span>
                      <textarea
                        value={profileBio}
                        onChange={(e) => setProfileBio(e.target.value.slice(0, 150))}
                        placeholder="Write a short bio about yourself"
                        maxLength={150}
                        rows={2}
                        className="wam-modal-textarea"
                      />
                      <div className="wam-char-count">{profileBio.length}/150</div>
                    </div>

                    <button
                      className="wam-modal-submit"
                      onClick={handleSaveProfile}
                      disabled={!hasProfileChanges || isSavingProfile}
                    >
                      {isSavingProfile ? (
                        <FontAwesomeIcon icon={faSpinner} spin />
                      ) : (
                        'Save changes'
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppManager;